import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { FilterOptions, Booking } from '@/types';
import { bookingService, type BookingFilters, type CreateBookingData, type UpdateBookingData } from '@/services/bookingService';

interface BookingsState {
  bookings: Booking[];
  selectedBooking: Booking | null;
  filters: BookingFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  stats: {
    totalBookings: number;
    activeBookings: number;
    completedBookings: number;
    cancelledBookings: number;
    totalRevenue?: number;
  } | null;
  loading: boolean;
  error: string | null;
}

// Async thunks for API operations
export const fetchBookings = createAsyncThunk(
  'bookings/fetchBookings',
  async ({ filters, page, limit }: { filters?: BookingFilters; page?: number; limit?: number }) => {
    const response = await bookingService.getBookings(filters || {}, page || 1, limit || 20);
    return response;
  }
);

export const fetchBooking = createAsyncThunk(
  'bookings/fetchBooking',
  async (id: string) => {
    const booking = await bookingService.getBooking(id);
    return booking;
  }
);

export const createBooking = createAsyncThunk(
  'bookings/createBooking',
  async (bookingData: CreateBookingData) => {
    const booking = await bookingService.createBooking(bookingData);
    return booking;
  }
);

export const updateBookingThunk = createAsyncThunk(
  'bookings/updateBooking',
  async ({ id, data }: { id: string; data: UpdateBookingData }) => {
    const booking = await bookingService.updateBooking(id, data);
    return booking;
  }
);

export const updateBookingStatus = createAsyncThunk(
  'bookings/updateBookingStatus',
  async ({ id, status, reason }: { id: string; status: string; reason?: string }) => {
    const booking = await bookingService.updateBookingStatus(id, status, reason);
    return booking;
  }
);

export const checkinBooking = createAsyncThunk(
  'bookings/checkinBooking',
  async ({ id, mileage, photos }: { id: string; mileage: number; photos?: string[] }) => {
    const booking = await bookingService.checkinBooking(id, mileage, photos);
    return booking;
  }
);

export const checkoutBooking = createAsyncThunk(
  'bookings/checkoutBooking',
  async ({ id, mileage, photos }: { id: string; mileage: number; photos?: string[] }) => {
    const booking = await bookingService.checkoutBooking(id, mileage, photos);
    return booking;
  }
);

export const fetchBookingStats = createAsyncThunk(
  'bookings/fetchStats',
  async () => {
    const stats = await bookingService.getBookingStats();
    return stats;
  }
);

const initialState: BookingsState = {
  bookings: [],
  selectedBooking: null,
  filters: {
    search: '',
    status: [],
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },
  stats: null,
  loading: false,
  error: null,
};

const bookingsSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    setBookings: (state, action: PayloadAction<Booking[]>) => {
      state.bookings = action.payload;
    },
    addBooking: (state, action: PayloadAction<Booking>) => {
      state.bookings.unshift(action.payload);
    },
    updateBooking: (state, action: PayloadAction<Booking>) => {
      const index = state.bookings.findIndex(b => b.id === action.payload.id);
      if (index !== -1) {
        state.bookings[index] = action.payload;
      }
      if (state.selectedBooking?.id === action.payload.id) {
        state.selectedBooking = action.payload;
      }
    },
    removeBooking: (state, action: PayloadAction<string>) => {
      state.bookings = state.bookings.filter(b => b.id !== action.payload);
      if (state.selectedBooking?.id === action.payload) {
        state.selectedBooking = null;
      }
    },
    setSelectedBooking: (state, action: PayloadAction<Booking | null>) => {
      state.selectedBooking = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<BookingFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setStats: (state, action: PayloadAction<any>) => {
      state.stats = action.payload;
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setPagination: (state, action: PayloadAction<Partial<typeof initialState.pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch bookings
    builder
      .addCase(fetchBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload.bookings as any;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch bookings';
      });

    // Fetch single booking
    builder
      .addCase(fetchBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedBooking = action.payload as any;
      })
      .addCase(fetchBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch booking';
      });

    // Create booking
    builder
      .addCase(createBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings.unshift(action.payload as any);
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create booking';
      });

    // Update booking
    builder
      .addCase(updateBookingThunk.fulfilled, (state, action) => {
        const index = state.bookings.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.bookings[index] = action.payload as any;
        }
        if (state.selectedBooking?.id === action.payload.id) {
          state.selectedBooking = action.payload as any;
        }
      });

    // Update booking status
    builder
      .addCase(updateBookingStatus.fulfilled, (state, action) => {
        const index = state.bookings.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.bookings[index] = action.payload as any;
        }
        if (state.selectedBooking?.id === action.payload.id) {
          state.selectedBooking = action.payload as any;
        }
      });

    // Check-in booking
    builder
      .addCase(checkinBooking.fulfilled, (state, action) => {
        const index = state.bookings.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.bookings[index] = action.payload as any;
        }
        if (state.selectedBooking?.id === action.payload.id) {
          state.selectedBooking = action.payload as any;
        }
      });

    // Check-out booking
    builder
      .addCase(checkoutBooking.fulfilled, (state, action) => {
        const index = state.bookings.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.bookings[index] = action.payload as any;
        }
        if (state.selectedBooking?.id === action.payload.id) {
          state.selectedBooking = action.payload as any;
        }
      });

    // Fetch stats
    builder
      .addCase(fetchBookingStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const {
  setBookings,
  addBooking,
  updateBooking,
  removeBooking,
  setSelectedBooking,
  setFilters,
  setStats,
  clearFilters,
  setPagination,
  setLoading,
  setError,
} = bookingsSlice.actions;

export default bookingsSlice.reducer;

// Selectors
export const selectBookings = (state: { bookings: BookingsState }) => state.bookings;
export const selectBookingsList = (state: { bookings: BookingsState }) => state.bookings.bookings;
export const selectSelectedBooking = (state: { bookings: BookingsState }) => state.bookings.selectedBooking;
export const selectBookingsLoading = (state: { bookings: BookingsState }) => state.bookings.loading;
export const selectBookingsError = (state: { bookings: BookingsState }) => state.bookings.error;
export const selectBookingsStats = (state: { bookings: BookingsState }) => state.bookings.stats;
export const selectBookingsPagination = (state: { bookings: BookingsState }) => state.bookings.pagination;
export const selectBookingsFilters = (state: { bookings: BookingsState }) => state.bookings.filters;